// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import Fraction from 'fraction.js';
import Latex from './Latex.js';
import color from './color.js';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import Figure from './Figure.js';
import { figureSizes, illustrationColor } from './designSystem.js';

const { abs, round } = Math;

const scale = ({ domain, range }) => {
  const domainInterval = domain[1] - domain[0];
  const rangeInterval = abs(range[1] - range[0]);
  const scalar = rangeInterval / domainInterval;
  return function toPx(x) {
    return (x - domain[0]) * scalar;
  };
};

const validateTickInterval = (interval: mixed): boolean => {
  return interval != null ? typeof interval === 'number' && interval > 0 : true;
};

const createLogicalTickValues = (min, max, interval) => {
  const ticks = [];

  if (min > 0 || max < 0) {
    // This is the case where its all in the +ve or all in the -ve
    // NOTE there will be a subtle off-by-1 bug here if you choose your max
    // to be a non-multiple of the interval.
    const numTicks = round((max - min) / interval) + 1;

    for (let i = 0; i < numTicks; i++) ticks.push(min + i * interval);
  } else {
    const posRange = max;
    const negRange = abs(min);
    const posTicks = round(posRange / interval);
    const negTicks = round(negRange / interval);
    for (let i = 0; i <= posTicks; i++) ticks.push(i * interval);
    for (let i = 1; i <= negTicks; i++) ticks.push(-(i * interval));
    ticks.sort((a, b) => a - b); // output is more sensible in sorted order.
  }

  return ticks;
};

const NumberLineContext = React.createContext<{|
  toPx: (x: number) => number,
  width: number,
|}>({
  toPx: (x) => x,
  width: 0,
});

type NumberLineComponent = typeof NLPoint | typeof NLInterval; // eslint-disable-line

type Props = {|
  width: number,
  height: number,
  min: number,
  max: number,
  majorTickInterval?: number,
  minorTickInterval?: number,
  unit?: string, // latex
  numberFormat?: 'decimal' | 'mixedFraction' | 'improperFraction',
  children?: ChildrenArray<Element<NumberLineComponent>>,
  // There are certain cases where we don't want <Figure> to
  // apply its default margins. At time of writing, SolidifyFigureGrid
  // and SolidifyAnnotatedFigure both require this. I'm not really happy
  // with this approach though, so need to rethink it sometime.
  _removeFigureMargin?: boolean,
  ...CompilerInjectedProps,
|};

// The distance beyond the min<->max range on each side that we extend
// the number line and show the arrow heads.
const ARROW_PADDING = 20;
const DEFAULT_WIDTH = 596;
const DEFAULT_HEIGHT = 160;

const NumberLine = (props: Props) => {
  const { failedValidation, addErrors } = usePropsValidation(props, NumberLine);
  const invalidMajorTick = !validateTickInterval(props.majorTickInterval);
  const invalidMinorTick = !validateTickInterval(props.minorTickInterval);

  // Ad-hoc assertion: ensure that the dimensions are one of the allowed figure sizes from our design system.
  React.useEffect(() => {
    if (!props._performValidation) return;
    const { width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT } = props;

    if (invalidMajorTick) {
      addErrors([
        {
          message: "majorTickInterval can't be less than or equal to 0",
          ...props._sourceLocations?.majorTickInterval,
        },
      ]);
    }

    if (invalidMinorTick) {
      addErrors([
        {
          message: "minorTickInterval can't be less than or equal to 0",
          ...props._sourceLocations?.minorTickInterval,
        },
      ]);
    }

    if (
      figureSizes.find((s) => s.width === width && s.height === height) !==
      undefined
    )
      return;

    const allowedSizesMsg = figureSizes
      .map((s) => `- ${s.width}x${s.height}`)
      .join('\n');
    addErrors([
      {
        message: [
          `NumberLine cannot be ${width}x${height}.`,
          `You must use one of the following dimensions:`,
          allowedSizesMsg,
        ].join('\n'),
        ...props._sourceLocations?.NumberLine,
      },
    ]);
  });

  if (failedValidation || invalidMajorTick || invalidMinorTick) {
    return null;
  }

  const {
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
    min,
    max,
    majorTickInterval = 1,
    minorTickInterval,
    unit = '',
    numberFormat = 'decimal',
    children,
  } = props;
  const strokeWidth = 1;
  const toPx = scale({
    domain: [min, max],
    range: [0, width - strokeWidth - ARROW_PADDING * 2],
  });
  window.toPx = toPx;

  const tickValues = createLogicalTickValues(min, max, majorTickInterval);

  return (
    <NumberLineContext.Provider value={{ toPx, width }}>
      <Figure _removeFigureMargin={props._removeFigureMargin}>
        <div style={{ position: 'relative' }}>
          <svg
            width={width}
            viewBox={`0 0 ${width} ${height}`}
            stroke={color.ebony}
            style={{ display: 'inline-block' }}
          >
            {/* Minor ticks */}
            {minorTickInterval !== undefined && (
              <g
                // TODO Why do we need the +0.5? i thought the line would get +.5 at top and bottom
                transform={`translate(${ARROW_PADDING + strokeWidth / 2}, ${
                  height / 2 - minorTickHeight / 2 + 0.5
                })`}
              >
                {createLogicalTickValues(min, max, minorTickInterval).map(
                  (value) => (
                    <Tick
                      key={value}
                      value={value}
                      toPx={toPx}
                      strokeWidth={strokeWidth}
                      isMinorTick
                    />
                  ),
                )}
              </g>
            )}

            {/* Major ticks */}
            <g
              // TODO Why do we need the +0.5? i thought the line would get +.5 at top and bottom
              transform={`translate(${ARROW_PADDING + strokeWidth / 2}, ${
                height / 2 - majorTickHeight / 2 + 0.5
              })`}
            >
              {tickValues.map((value) => (
                <Tick
                  key={value}
                  value={value}
                  toPx={toPx}
                  strokeWidth={strokeWidth}
                />
              ))}
            </g>

            {/* Major tick labels */}
            {tickValues.map((value) => (
              <foreignObject
                key={value}
                overflow="visible"
                // 🚨 Warning 🚨
                // There is a really shit bug in Safari in which all SVG transforms in the ancestor
                // chain are discarded if an html element has a stacking context / gets its own layer
                // (roughly speaking). There are heaps of things that cause this: transparency, transforms,
                // relative position, etc. In our case, we use translateX(-50%) for center-aligning the
                // latex, which triggers this. Additionally (and more problematically) in Katex internals
                // they rely on position: relative on the .base span, which also triggers it. We can't
                // easily avoid this Katex behaviour so we need to take care to avoid any SVG transforms
                // in the ancestor chain and instead use x/y coordinates directly on foreignObject.
                x={ARROW_PADDING + strokeWidth / 2 + toPx(value)}
                y={height / 2}
                // Firefox will incorrectly cull <foreignObject> from rendering unless
                // a non-zero width and height are specified. These values are arbitrary
                // as our subtree is rendered by virtue of overflow="visible"
                width={1}
                height={1}
              >
                <span
                  style={{
                    display: 'inline-block',
                    transform: 'translateX(-50%)', // center-align to tick
                    fontSize: 14,
                  }}
                >
                  <LabelText
                    representation={numberFormat}
                    value={value}
                    unit={unit}
                  />
                </span>
              </foreignObject>
            ))}

            {/* Line with arrow heads */}
            <g
              transform={`translate(${(strokeWidth / 2) * 0}, ${
                height / 2 - 3.5
              })`}
            >
              <path
                d={[
                  'M 5 3',
                  'v -3',
                  'l -5 3.5',
                  'l 5 3.5',
                  'v -3',
                  `h ${width - 5 * 2}`,
                  'v 3',
                  'l 5 -3.5',
                  'l -5, -3.5',
                  'v 3',
                  `h ${-width - 5 * 2}`,
                  'Z',
                ].join(' ')}
                stroke="none"
              />
            </g>
            <g
              // TODO Why do we need the +0.5? i thought the line would get +.5 at top and bottom
              transform={`translate(${ARROW_PADDING + strokeWidth / 2}, ${
                height / 2 - 5 + 0.5
              })`}
            >
              {children}
            </g>
          </svg>
        </div>
      </Figure>
    </NumberLineContext.Provider>
  );
};
NumberLine.spec = {
  // width and height are now required, but we have maintained the old
  // default logic so current content does not break.
  // We will unwind it when existing content has been updated to
  // reflect the new validation.
  width: t.number,
  height: t.number,
  min: t.number,
  max: t.number,
  majorTickInterval: t.optional(t.number),
  minorTickInterval: t.optional(t.number),
  unit: t.optional(t.latex),
  numberFormat: t.optional(
    t.unionOfValues('decimal', 'mixedFraction', 'improperFraction'),
  ),
  children: t.optional(
    t.childrenArray(
      t.unionOfTypes(t.elementType(NLPoint), t.elementType(NLInterval)),
    ),
  ),
};

const majorTickHeight = 20;
const minorTickHeight = majorTickHeight / 2;
const Tick = ({ value, toPx, strokeWidth, isMinorTick = false }) => (
  <line
    x1={toPx(value)}
    y1={0}
    x2={toPx(value)}
    y2={isMinorTick ? minorTickHeight : majorTickHeight}
    strokeWidth={strokeWidth}
  />
);

type PointProps = {| value: number |};
function NLPoint(props: PointProps) {
  const { failedValidation } = usePropsValidation(props, NLPoint);
  const ctx = React.useContext(NumberLineContext);
  if (failedValidation) return null;
  if (ctx === null) return null;
  const { value } = props;
  const { toPx } = ctx;
  return (
    <circle
      cx={toPx(value)}
      cy={3 + 1.5}
      r={9}
      fill={color.purpleHeart}
      stroke="none"
    />
  );
}
NLPoint.spec = {
  value: t.number,
};

const intervalColor = illustrationColor.niagra;
type IntervalProps = {|
  min: number,
  minInclusive: boolean,
  max: number,
  maxInclusive: boolean,
|};
function NLInterval(props: IntervalProps) {
  const { failedValidation } = usePropsValidation(props, NLInterval);
  const ctx = React.useContext(NumberLineContext);
  if (failedValidation) return null;
  if (ctx === null) return null;
  const { min, minInclusive, max, maxInclusive } = props;
  const { toPx, width } = ctx;
  return (
    <>
      <IntervalLine min={min} max={max} toPx={toPx} width={width} />
      {min !== -Infinity && (
        <circle
          cx={toPx(min)}
          cy={3 + 1.5}
          r={7}
          fill={minInclusive ? intervalColor : 'none'}
          stroke={intervalColor}
          strokeWidth={4}
        />
      )}
      {max !== Infinity && (
        <circle
          cx={toPx(max)}
          cy={3 + 1.5}
          r={7}
          fill={maxInclusive ? intervalColor : 'none'}
          stroke={intervalColor}
          strokeWidth={4}
        />
      )}
    </>
  );
}
NLInterval.spec = {
  min: t.number,
  minInclusive: t.boolean,
  max: t.number,
  maxInclusive: t.boolean,
};

const IntervalLine = ({ min, max, toPx, width }) => {
  const arrowHeadWidth = 7.5;
  let startX;
  if (min === -Infinity) {
    // TODO why do we need the -1? Why are we inset by 1px relative to main line?
    startX = 0 - ARROW_PADDING - 1 + arrowHeadWidth;
  } else {
    startX = toPx(min) + 6;
  }

  let stopX;
  if (max === Infinity) {
    stopX = width - ARROW_PADDING - arrowHeadWidth;
  } else {
    stopX = toPx(max) - 6;
  }

  const y = 3 + 1.5;

  return (
    <>
      <line
        x1={startX}
        y1={y}
        x2={stopX}
        y2={y}
        stroke={intervalColor}
        strokeWidth={4}
      />
      {min === -Infinity && (
        <path
          d={[`M ${0 - ARROW_PADDING - 1} ${y}`, 'l 7.5 5', 'v -10', 'Z'].join(
            ' ',
          )}
          fill={intervalColor}
          stroke="none"
        />
      )}
      {max === Infinity && (
        <path
          d={[`M ${width - ARROW_PADDING} ${y}`, 'l -7.5 5', 'v -10', 'Z'].join(
            ' ',
          )}
          fill={intervalColor}
          stroke="none"
        />
      )}
    </>
  );
};

const LabelText = ({ representation, value, unit = '' }) => {
  let latex = '';

  // TODO do exhaustive "pattern" matching here
  if (representation === 'improperFraction') {
    latex = new Fraction(value).toLatex();
  } else if (representation === 'mixedFraction') {
    latex = new Fraction(value).toLatex(true);
  } else if (representation === 'decimal') {
    latex = Number.isInteger(value) ? String(value) : String(value.toFixed(1)); // TODO allow specifying precision?
  }

  latex += unit;

  return <Latex expression={latex} displayMode />;
};

export { NumberLine, NLPoint, NLInterval };
